import type { Component } from 'vue'

/**
 * Sidebar section for grouped nav (see {@link NAV_GROUP_ORDER}).
 * `dashboard` is rendered without an AppSidebarGroup header.
 */
export type PortalNavGroupId = 'dashboard' | 'learningCommunity' | 'financeBilling' | 'businessOperations'

/** Labeled groups in visual order (dashboard is handled separately). */
export const NAV_GROUP_ORDER: Array<Exclude<PortalNavGroupId, 'dashboard'>> = ['learningCommunity', 'financeBilling', 'businessOperations']

/** i18n keys for AppSidebarGroup headers. */
export const NAV_GROUP_LABEL_KEYS: Record<Exclude<PortalNavGroupId, 'dashboard'>, string> = {
  learningCommunity: 'clientportal.sidebar.navGroup.learningCommunity',
  financeBilling: 'clientportal.sidebar.navGroup.financeBilling',
  businessOperations: 'clientportal.sidebar.navGroup.businessOperations',
}

/**
 * Navigation item configuration for the ClientPortal sidebar.
 * Icons use Nuxt Icons custom collection app-sidebar-icons (normal + *-active variant).
 */
export interface NavigationItemConfig {
  id: string
  translationKey: string // Key for t() from useI18n()
  /** Base icon name for app-sidebar-icons (e.g. 'dashboard' → dashboard.svg / dashboard-active.svg). Omit when using fallbackIcon only. */
  iconName?: string
  /** Fallback component when iconName has no SVG assets (e.g. estimates) */
  fallbackIcon?: Component
  routeName: string // Route name for CPNavigateTo
  routePath: string // Path for matching current route
  hidden?: boolean
  /** When true, item is omitted from the mobile drawer sidebar only (desktop sidebar unchanged). */
  hideOnMobile?: boolean
  /** Sidebar section; dashboard row has no group header. */
  navGroup: PortalNavGroupId
}

/**
 * Static navigation items configuration
 * Labels are fetched via translation keys at runtime
 */
export const NAVIGATION_ITEMS: NavigationItemConfig[] = [
  {
    id: 'dashboard',
    translationKey: 'clientportal.navigation.dashboard',
    iconName: 'dashboard',
    routeName: 'clientportal-dashboard',
    routePath: '/dashboard',
    navGroup: 'dashboard',
  },
  {
    id: 'communities',
    translationKey: 'clientportal.navigation.communities',
    iconName: 'communities',
    routeName: 'clientportal-communities',
    routePath: '/communities',
    navGroup: 'learningCommunity',
  },
  {
    id: 'courses',
    translationKey: 'clientportal.navigation.courses',
    iconName: 'courses',
    routeName: 'clientportal-courses',
    routePath: '/courses',
    navGroup: 'learningCommunity',
  },
  {
    id: 'documents',
    translationKey: 'clientportal.navigation.sharedFiles',
    iconName: 'documents',
    routeName: 'clientportal-shared-files',
    routePath: '/shared-files',
    navGroup: 'businessOperations',
  },
  {
    id: 'invoices',
    translationKey: 'clientportal.navigation.invoices',
    iconName: 'invoices',
    routeName: 'clientportal-invoices',
    routePath: '/invoices',
    navGroup: 'financeBilling',
  },
  {
    id: 'estimates',
    translationKey: 'clientportal.navigation.estimates',
    iconName: 'estimates',
    routeName: 'clientportal-estimates',
    routePath: '/estimates',
    navGroup: 'financeBilling',
  },
  {
    id: 'contracts',
    translationKey: 'clientportal.navigation.contracts',
    iconName: 'contracts',
    routeName: 'clientportal-contracts',
    routePath: '/contracts',
    navGroup: 'financeBilling',
  },
  {
    id: 'appointments',
    translationKey: 'clientportal.navigation.appointments',
    iconName: 'appointments',
    routeName: 'clientportal-appointments',
    routePath: '/appointments',
    hidden: true,
    navGroup: 'businessOperations',
  },
  {
    id: 'affiliates',
    translationKey: 'clientportal.navigation.affiliates',
    iconName: 'affiliates',
    routeName: 'clientportal-affiliates',
    routePath: '/affiliates',
    hideOnMobile: true,
    navGroup: 'businessOperations',
  },
]
